// src/pages/GraficoRealVsRemu.jsx
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Cell
} from 'recharts';

const FONT_STACK = 'Inter, Roboto, Arial, Helvetica, sans-serif';
const TICK_FONT_SIZE = 11;

const N = (v) => (v == null || v === '' || isNaN(Number(v)) ? 0 : Number(v));
const fmtPct = (v) => `${(isFinite(v) ? v : 0).toFixed(2).replace('.', ',')}%`;
const fmtPctAbs = (v) => `${Math.abs(isFinite(v) ? v : 0).toFixed(2).replace('.', ',')}%`;
const fmtBRL = (v) =>
  (isFinite(v) ? v : 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function wrapLabel(value, maxPerLine = 16, maxLines = 3) {
  const words = String(value ?? '').split(/\s+/);
  const lines = [];
  let line = '';
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    const test = line ? `${line} ${w}` : w;
    if (test.length > maxPerLine) {
      if (line) lines.push(line);
      line = w;
    } else {
      line = test;
    }
    if (lines.length === maxLines - 1 && i < words.length - 1) {
      const rest = [line, ...words.slice(i + 1)].join(' ');
      lines.push(rest);
      return lines.slice(0, maxLines);
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, maxLines);
}

const XTickBase = ({ x, y, payload, color }) => {
  const lines = wrapLabel(payload?.value ?? '', 16, 3);
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="middle"
        dominantBaseline="hanging"
        fontFamily={FONT_STACK}
        fontSize={TICK_FONT_SIZE}
        fill={color}
      >
        {lines.map((ln, i) => (
          <tspan key={i} x="0" dy={i === 0 ? 10 : 14}>
            {ln}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: .5, fontFamily: FONT_STACK }}>
        Atividade: {label}
      </Typography>
      <Typography variant="caption" display="block" sx={{ fontFamily: FONT_STACK }}>
        Diferença (%): <b>{fmtPct(p.difPct)}</b>
      </Typography>
      <Typography variant="caption" display="block" sx={{ fontFamily: FONT_STACK }}>
        Gasto Real: <b>{fmtBRL(p.real)}</b>
      </Typography>
      <Typography variant="caption" display="block" sx={{ fontFamily: FONT_STACK }}>
        Custo Remunerado IMMU: <b>{fmtBRL(p.custoRemu)}</b>
      </Typography>
      <Typography variant="caption" display="block" sx={{ fontFamily: FONT_STACK }}>
        Diferença (R$): <b>{fmtBRL(p.real - p.custoRemu)}</b>
      </Typography>
    </Box>
  );
};

export default function GraficoRealVsRemu({ rows = [], selectedGroup, kmAcopTotal = 0, topN = 20 }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const textColor = isDark ? '#FFFFFF' : '#303030';

  // ===== 1) Dataset do gráfico a partir do grupo selecionado (sempre executa) =====
  const data = useMemo(() => {
    if (!selectedGroup) return [];
    const map = new Map();

    for (const r of rows) {
      if (String(r.GR_ANALISE).trim() !== String(selectedGroup).trim()) continue;
      const key = r.GR_COMPRA || '—';
      const real = N(r.GASTO_REAL);
      const immu = N(r.IMMU); // custo/km
      if (!map.has(key)) map.set(key, { compra: key, real: 0, immu: 0 });
      const acc = map.get(key);
      acc.real += real;
      acc.immu += immu;
    }

    const arr = [...map.values()].map((it) => {
      const custoRemu = kmAcopTotal > 0 ? it.immu * kmAcopTotal : 0;
      const difAbs = it.real - custoRemu;
      const difPct = custoRemu ? (difAbs / custoRemu) * 100 : 0;
      return { ...it, custoRemu, difAbs, difPct };
    });

    // Top N por |dif%|
    arr.sort((a, b) => Math.abs(b.difPct) - Math.abs(a.difPct));
    return arr.slice(0, topN);
  }, [rows, selectedGroup, kmAcopTotal, topN]);

  // Assinatura também com VALORES (garante remontagem quando números mudam)
  const dataSignature = useMemo(
    () => data.map(d => `${d.compra}:${Number.isFinite(d.difPct) ? d.difPct.toFixed(6) : '0'}`).join('|'),
    [data]
  );

  // ===== 2) Força remontagem do chart e do rodapé (sempre definidos antes de qualquer return) =====
  const chartKey  = `${selectedGroup || 'none'}|${dataSignature}`;
  const footerKey = `footer|${chartKey}`;

  // ===== 3) RODAPÉ — calcula SEMPRE a partir do dataset atual (hooks antes de returns) =====
  const worst = useMemo(() => {
    if (!data.length) return null;
    let candidate = null;
    for (const d of data) {
      if (d.difPct > 0 && (!candidate || d.difPct > candidate.difPct)) candidate = d;
    }
    return candidate;
  }, [data]);

  const best = useMemo(() => {
    if (!data.length) return null;
    let candidate = null;
    for (const d of data) {
      if (d.difPct < 0 && (!candidate || d.difPct < candidate.difPct)) candidate = d;
    }
    return candidate;
  }, [data]);

  // ===== 4) Early returns (agora SEMPRE após todos os hooks) =====
  if (!selectedGroup) {
    return (
      <Box sx={{
        mt: 3, p: 4, border: '2px dashed #0ea5e9', borderRadius: 4,
        bgcolor: 'rgba(14,165,233,0.05)', textAlign: 'center'
      }}>
        <Typography variant="body1" sx={{ fontFamily: FONT_STACK, fontSize: 11, color: textColor }}>
          Selecione um <b>GRUPO</b> para visualizar o gráfico de desvios por <b>Itens</b>.
        </Typography>
      </Box>
    );
  }

  if (!data.length) {
    return (
      <Box sx={{ mt: 3, textAlign: 'center', opacity: .7 }}>
        <Typography variant="body2" sx={{ fontFamily: FONT_STACK, fontSize: 12, color: textColor }}>
          Sem dados de Itens para este grupo no período.
        </Typography>
      </Box>
    );
  }

  // ===== 5) Valores do rodapé (podem ficar aqui, não são hooks) =====
  const worstDiffBRL = worst ? (worst.real - worst.custoRemu) : 0;
  const bestDiffBRL  = best  ? (best.real  - best.custoRemu)  : 0;

  return (
    <Box sx={{ mt: 3, p: 2, border: '2px solid #0ea5e9', borderRadius: 4, width: '100%' }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 800,
          textAlign: 'center',
          mb: 1,
          textTransform: 'uppercase',
          fontFamily: FONT_STACK,
          fontSize: 20,
          color: textColor
        }}
      >
        Análise de Gasto Real vs. Custo Remunerado — Diferença (%) por Item
      </Typography>

      <Box sx={{ height: 480 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            key={chartKey}
            data={data}
            margin={{ top: 10, right: 10, left: 10, bottom: 70 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="compra"
              height={72}
              tick={(props) => <XTickBase {...props} color={textColor} />}
            />
            <YAxis
              tickFormatter={fmtPct}
              tick={{ fontSize: TICK_FONT_SIZE, fontFamily: FONT_STACK, fill: textColor }}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <ReferenceLine y={0} stroke={isDark ? '#bbb' : '#777'} />
            <Bar
              dataKey="difPct"
              name="Diferença (%)"
              isAnimationActive={false}
              animationBegin={0}
              animationDuration={0}
              animationEasing="linear"
            >
              {data.map((d) => (
                <Cell key={`cell-${d.compra}`} fill={d.difPct >= 0 ? '#FF4E50' : '#2ecc71'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box key={footerKey} sx={{
        mt: 1,
        textAlign: 'center',
        opacity: 0.9,
        display: 'grid',
        gap: .25,
        '& .MuiTypography-root': { fontSize: 14 }   // << ajuste aqui (px)
      }}>
        {worst && (
          <Typography variant="caption" sx={{ fontFamily: FONT_STACK, color: textColor }}>
            <b>Item Crítico:</b> {worst.compra} — Remuneração de {fmtBRL(worst.custoRemu)}, Gasto Real {fmtBRL(worst.real)}{' '}
            (<b>Diferença de {fmtBRL(worstDiffBRL)} Negativa</b>, {fmtPctAbs(worst.difPct)} acima do custo remunerado).
          </Typography>
        )}
        {best && (
          <Typography variant="caption" sx={{ fontFamily: FONT_STACK, color: textColor }}>
            <b>Destaque Positivo:</b> {best.compra} — Remuneração de {fmtBRL(best.custoRemu)}, Gasto Real {fmtBRL(best.real)}{' '}
            (<b>Diferença de {fmtBRL(bestDiffBRL)} Positiva</b>, {fmtPctAbs(best.difPct)} abaixo do custo remunerado).
          </Typography>
        )}
      </Box>
    </Box>
  );
}
